import type { WorkspaceMedia } from '../db/typeRoots/workspaceMedia'
import { requiresAiDisclosure } from '../db/typeRoots/workspaceMedia'

/**
 * Single source of truth for how we disclose AI-generated media, and how each
 * platform lets us do it. Both the publish path and the workspace UI read this,
 * so the control a user sees and the request we send can never drift apart.
 *
 * Verified against first-party platform docs in August 2026. Supersedes the
 * matrix in GH #118, which predates Instagram shipping `is_ai_generated`.
 *
 * @see https://developers.facebook.com/docs/instagram-platform/content-publishing
 * @see https://developers.facebook.com/docs/graph-api/reference/page/photos/
 * @see https://developers.google.com/youtube/v3/docs/videos
 */

/**
 * LEGACY. Superseded by `AiDisclosureChoice`, which carries the same two
 * mechanisms as independent switches.
 *
 * The ladder encoded a belief the live testing disproved: that a platform's own
 * flag is a weak disclosure because it hides behind an "AI info" menu, so a
 * visible label had to be the strictly stronger rung above it. On Instagram and
 * Facebook the native label is on the post surface, directly under the account
 * name. The two mechanisms are not weak and strong versions of one thing, they
 * do different jobs: a platform flag is an affordance that vanishes when content
 * is reshared or downloaded, and a baked pill is a durable mark that survives
 * both. Ordering them on one axis forced a choice that isn't one.
 *
 * Retained only to read workspaces saved under the old model. See
 * `aiDisclosureFromLegacyLevel`.
 *
 * The two "on" levels exist because the obligations genuinely differ. EU AI Act
 * Art. 50(4) requires a deployer to disclose image content "constituting a deep
 * fake" — Art. 3(60) defines that as content resembling real persons, objects,
 * places or events that "would falsely appear to a person to be authentic".
 * Obviously-styled marketing artwork does not meet that bar, so labelling it
 * would deface the creative for no compliance gain.
 *
 * Separately, platform policy (TikTok, YouTube, Meta Ads) requires
 * self-disclosure for realistic synthetic media regardless of the AI Act. The
 * lower level satisfies that at no visual cost.
 */
export type AiDisclosureLevel =
  /** No disclosure. */
  | 'off'
  /**
   * AI-generated or AI-edited, but not a realistic depiction of real people,
   * places or events. Sets the platform's own AI flag; artwork untouched.
   */
  | 'aiGenerated'
  /**
   * A realistic depiction a viewer could take as authentic — the Art. 50(4)
   * "deep fake" line. Sets the platform flag AND bakes a visible label into the
   * image.
   *
   * The stated reason for this rung was that a platform-side label is "often
   * reachable only through a menu", against Commission guidance that disclosure
   * is "not satisfied if only in a manual or hidden under menu layers". Live
   * testing disproved that for Meta: Instagram and Facebook both show their
   * label on the post surface. The surviving reason is narrower — no
   * platform-side label survives a reshare or a download.
   */
  | 'realisticDepiction'

/** How a platform's own AI label gets set. */
export type AiDisclosureMechanism =
  /** A boolean in the publish payload flips the platform's own label. */
  | 'nativeFlag'
  /** The platform reads provenance and we declare what our file carries. */
  | 'nativeProvenance'
  /** No API affordance at all. Only our own baked label can disclose. */
  | 'none'

export type AiDisclosureCapability = {
  mechanism: AiDisclosureMechanism
  /** The platform field we set, for logs and UI copy. `null` when there is none. */
  apiField: string | null
  /**
   * Whether the platform auto-labels from embedded file metadata. Our IPTC stamp
   * (GH #118) rides along on any upload whose media genuinely has AI provenance
   * — see `resolveAiProvenanceClaim`, which is also what stops it riding along on
   * media that has none.
   *
   * Both values here are from live posts, not docs. Instagram labelled a story we
   * sent no flag on at all. TikTok did not label a photo carrying the marker and
   * a visible pill with the flag off.
   */
  readsEmbeddedIptc: boolean
  /** Media kinds this platform publishes. Video can't carry a baked label —
   *  there is no video compositor on the publish path — so a video-only
   *  platform can never satisfy the visible-label switch. Read by the resolver,
   *  not decorative: without it YouTube claimed a label it cannot render. */
  publishes: ReadonlyArray<'image' | 'video'>
  /**
   * Media kinds the platform's own flag actually covers. NOT the same as
   * `publishes`: a platform can publish both kinds while its disclosure field
   * only exists on one endpoint.
   *
   * This distinction is load-bearing. Both fields we set are narrower than the
   * platform's media range, and neither platform errors on the extra field —
   * they accept the post and drop it, so the failure is invisible:
   *
   *   facebook  `provenance_info` is a /photos parameter only. `createPost`
   *             sends it on the photo path and nowhere else, so a video post
   *             carries nothing.
   *
   * The inverse mistake is just as easy, and we made it: TikTok's `is_aigc` is
   * absent from the documented photo fields but works there anyway. Narrow this
   * on evidence that the field is ignored, not on a doc table's silence.
   *
   * Treating a flag as universal is therefore not a cosmetic inaccuracy: it
   * makes `resolveAiDisclosurePlan` claim a disclosure that never happens, and
   * suppresses the `cannotDisclose` report that would otherwise tell the user.
   * Verify against the platform's own endpoint reference before widening either
   * entry.
   */
  nativeFlagCovers: ReadonlyArray<'image' | 'video'>
  /** One line of user-facing explanation of the platform's own label. Kept here
   *  so backend and UI never describe the same behaviour differently. */
  summary: string
}

export const PLATFORMS_WITH_AI_DISCLOSURE = [
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
] as const

export type AiDisclosurePlatform = (typeof PLATFORMS_WITH_AI_DISCLOSURE)[number]

export const AI_DISCLOSURE_CAPABILITY: Record<AiDisclosurePlatform, AiDisclosureCapability> = {
  instagram: {
    mechanism: 'nativeFlag',
    apiField: 'is_ai_generated',
    readsEmbeddedIptc: true,
    publishes: ['image', 'video'],
    // IMAGE / VIDEO / REELS / CAROUSEL, on the parent container.
    nativeFlagCovers: ['image', 'video'],
    summary: 'Instagram shows its own “AI info” label.',
  },
  facebook: {
    mechanism: 'nativeProvenance',
    apiField: 'provenance_info',
    readsEmbeddedIptc: true,
    publishes: ['image', 'video'],
    // A /photos parameter only; the video path has no equivalent.
    nativeFlagCovers: ['image'],
    summary: 'Facebook shows its own “AI info” label.',
  },
  tiktok: {
    mechanism: 'nativeFlag',
    apiField: 'is_aigc',
    // Confirmed by a controlled test: a photo carrying our IPTC stamp AND a
    // baked pill, published with `is_aigc` off, got no TikTok label at all.
    // TikTok reads its own flag, not our metadata.
    readsEmbeddedIptc: false,
    publishes: ['image', 'video'],
    /**
     * Photos as well as video, despite `is_aigc` being absent from TikTok's
     * documented photo `post_info` fields. A documentation gap is not a
     * capability gap, and we spent a while treating it as one.
     *
     * Established by isolating the variable rather than by reading docs: three
     * SELF_ONLY photo posts of the same unstamped JPEG, differing only in what
     * we sent. `is_aigc` alone produced "Creator labeled as AI-generated" within
     * two minutes. A baked pill plus a re-stamped IPTC marker, with the flag off,
     * produced nothing. Neither produced anything.
     *
     * One negative remains unexplained: an earlier photo post sent the same flag
     * and never showed a label, on a post that is still up. It is a single
     * uncontrolled observation against a clean controlled positive, so the
     * positive wins — but it is recorded rather than explained away.
     */
    nativeFlagCovers: ['image', 'video'],
    summary: 'TikTok shows its own “AI-generated” label.',
  },
  youtube: {
    mechanism: 'nativeFlag',
    apiField: 'status.containsSyntheticMedia',
    readsEmbeddedIptc: false,
    // Video only, so the visible label never applies here.
    publishes: ['video'],
    nativeFlagCovers: ['video'],
    summary: 'YouTube shows its “Altered or synthetic content” disclosure.',
  },
  linkedin: {
    // LinkedIn's "CR" badge is the C2PA Content Credentials badge and needs a
    // signed manifest from a trust-list CA, which we don't have (#118 Tier 2).
    // There is no AI-disclosure field anywhere in the Posts API schema.
    mechanism: 'none',
    apiField: null,
    readsEmbeddedIptc: false,
    publishes: ['image', 'video'],
    nativeFlagCovers: [],
    summary: 'LinkedIn has no AI label of its own.',
  },
}

/** Corner the visible label sits in. */
export const AI_LABEL_POSITIONS = ['bottomLeft', 'bottomRight', 'topLeft', 'topRight'] as const
export type AiLabelPosition = (typeof AI_LABEL_POSITIONS)[number]

/**
 * Appearance of the label we bake in. Fully user-controlled by design, so it can
 * be made to fit a brand rather than sitting on the artwork as a foreign object.
 *
 * Worth knowing what that freedom allows: these values can be set to produce a
 * mark that is technically present and effectively invisible — a low alpha, a
 * tiny scale, text the same colour as the pill. Commission placement guidance
 * expects a disclosure to stay legible when content is reshared or downloaded,
 * so a label dialled down that far stops being a disclosure while still looking
 * like one. The defaults are the tuned, legible values; nothing here enforces
 * them.
 */
export type AiLabelStyle = {
  position: AiLabelPosition
  /** Multiplier on the size the spec computes from the image. 1 = as tuned. */
  scale: number
  /** Pill background opacity, 0-1. */
  backgroundAlpha: number
  /** Pill fill, `#rrggbb`. */
  backgroundColor: string
  /** Label text colour, `#rrggbb`. */
  textColor: string
  /** Label copy. Short enough not to crowd the image. */
  text: string
}

export const DEFAULT_AI_LABEL_STYLE: AiLabelStyle = {
  position: 'bottomLeft',
  scale: 1,
  backgroundAlpha: 0.62,
  backgroundColor: '#000000',
  textColor: '#ffffff',
  text: 'AI generated',
}

/**
 * What disclosure the user asked for. Two independent switches rather than a
 * ladder, because the mechanisms are independent (see `AiDisclosureLevel`).
 *
 * Both off means no disclosure. Either on means the user asked for one, which is
 * what makes `cannotDisclose` meaningful: a request we cannot fulfil on a given
 * platform has to be reported rather than silently dropped.
 */
export type AiDisclosureChoice = {
  /** Ask each platform to set its own AI label, wherever it can. */
  nativeLabel: boolean
  /** Bake our own visible label into image media. */
  visibleLabel: boolean
  labelStyle: AiLabelStyle
  /**
   * Draw the label in the post preview. View-only — it changes nothing about
   * what publishes.
   *
   * On by default, and worth keeping that way: the preview used to omit the
   * label entirely, so a user approved a clean image and published one with a
   * pill burned in. Switching this off reintroduces that gap deliberately, which
   * is a different thing from doing it silently.
   */
  displayInPreview: boolean
  /** Draw the label over workspace media too, not just in the preview.
   *  View-only. Off by default: the workspace is where the artwork is edited,
   *  and a permanent overlay there would fight the editing view. */
  displayInWorkspace: boolean
}

export const AI_DISCLOSURE_OFF: AiDisclosureChoice = {
  nativeLabel: false,
  visibleLabel: false,
  labelStyle: DEFAULT_AI_LABEL_STYLE,
  displayInPreview: true,
  displayInWorkspace: false,
}

/**
 * Reads a workspace saved under the old level model.
 *
 * `realisticDepiction` maps to both switches on, which is what that level did.
 * `aiGenerated` maps to the native flag alone. Neither direction guesses: the
 * old data recorded a judgement about the content, and turning that into a
 * visible label the user never asked for would deface artwork retroactively.
 */
export const aiDisclosureFromLegacyLevel = (
  level: AiDisclosureLevel | undefined,
): AiDisclosureChoice => {
  if (level === 'realisticDepiction') {
    return { ...AI_DISCLOSURE_OFF, nativeLabel: true, visibleLabel: true }
  }
  if (level === 'aiGenerated') {
    return { ...AI_DISCLOSURE_OFF, nativeLabel: true }
  }
  return AI_DISCLOSURE_OFF
}

/** What a given choice actually does on a given platform. */
export type AiDisclosurePlan = {
  /** Set the platform's own AI flag. */
  nativeFlag: boolean
  /** Bake a visible label into image media. */
  bakedLabel: boolean
  /**
   * Disclosure was asked for and this platform can deliver none of it: no flag
   * covering this media kind, and no baked label either.
   *
   * Deliberately a *reported* state rather than an automatic fallback. An
   * earlier version appended "(AI generated)" to the caption here, which edits
   * copy the user wrote to satisfy our own compliance logic. Their caption is
   * theirs. Instead the UI states plainly that the platform will not disclose,
   * so the user can turn on the visible label — an informed choice rather than a
   * silent rewrite, without giving up the rule that nothing publishes as
   * undisclosed AI media by accident.
   */
  cannotDisclose: boolean
}

/**
 * Resolves the user's choice into concrete actions for one platform. The single
 * place that decides what each switch means, so the UI's explanation and the
 * publish path's behaviour come from the same function and cannot drift.
 *
 * `hasImageMedia` matters twice over. A baked label needs an image, and it also
 * decides whether the platform's own flag applies at all: several fields exist
 * on only one of the two media endpoints (see `nativeFlagCovers`).
 */
export const resolveAiDisclosurePlan = (
  choice: AiDisclosureChoice,
  platform: AiDisclosurePlatform,
  hasImageMedia: boolean,
): AiDisclosurePlan => {
  const capability = AI_DISCLOSURE_CAPABILITY[platform]

  // A platform's flag only counts if it covers the kind of media being posted.
  // Asking for it on the wrong endpoint is silently dropped, not rejected, so
  // trusting `mechanism` alone claims a disclosure that never reaches the post.
  const mediaKind = hasImageMedia ? 'image' : 'video'
  const nativeFlag =
    choice.nativeLabel &&
    capability.mechanism !== 'none' &&
    capability.nativeFlagCovers.includes(mediaKind)

  // A platform that cannot publish images cannot carry a baked label, however
  // much was asked for. Without this, YouTube reported "visible label added to
  // the image" on a video-only surface.
  const bakedLabel = choice.visibleLabel && hasImageMedia && capability.publishes.includes('image')

  // Only meaningful when something was actually requested. Both switches off is
  // a deliberate "no disclosure", not a failure to deliver one.
  const requested = choice.nativeLabel || choice.visibleLabel
  const cannotDisclose = requested && !nativeFlag && !bakedLabel

  return { nativeFlag, bakedLabel, cannotDisclose }
}

/** Whether any media attached to the post is AI-generated or AI-edited. Used to
 *  pre-fill the disclosure level; the user can always override it. */
export const mediaRequiresAiDisclosure = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>>,
) => media.some((m) => requiresAiDisclosure(m))

/**
 * What we switch on by default for a post's media.
 *
 * The native flag, never the visible label. Provenance metadata tells us an
 * image came from a model; it cannot tell us whether the image would pass as an
 * authentic photograph, which is the judgement that makes a burned-in mark worth
 * defacing artwork for. Guessing it wrong in one direction ruins a graphic and in
 * the other invents a reassurance nobody earned, so the user makes that call.
 */
export const defaultAiDisclosure = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>>,
): AiDisclosureChoice =>
  mediaRequiresAiDisclosure(media) ? { ...AI_DISCLOSURE_OFF, nativeLabel: true } : AI_DISCLOSURE_OFF

/**
 * What our IPTC marker should claim about a post's media, or `null` to claim
 * nothing at all.
 *
 * `null` is the important case and the one that was missing. Every re-stamp site
 * used to assert `generated` unconditionally, so posting a plain photograph that
 * happened to fall outside Instagram's aspect range — or simply turning the
 * visible label on — embedded a machine-readable claim that the photographer's
 * own image was synthetic. Nothing about that post involved AI.
 */
export type AiProvenanceClaim = 'generated' | 'composite' | null

/**
 * Resolves the claim from media provenance.
 *
 * `composite` wins over `generated` whenever any AI media is an edit or a
 * flattened canvas, because that direction is safe: `compositeWithTrainedAlgorithmicMedia`
 * says "contains model-produced elements", which stays true of a set that also
 * holds a fully generated image. The reverse is not true — claiming `generated`
 * over an edited photograph asserts the human parts are synthetic.
 *
 * A post mixing fully-generated and edited media therefore gets `composite` for
 * all of it. That is a known imprecision: the claim is post-level because the
 * re-stamp sites receive platform settings rather than per-item provenance, and
 * threading provenance through every `postContent` shape is a larger change than
 * the accuracy gain justifies for a rare case.
 */
export const resolveAiProvenanceClaim = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits' | 'type'>>,
): AiProvenanceClaim => {
  const disclosable = media.filter((m) => requiresAiDisclosure(m))
  if (disclosable.length === 0) return null

  const anyComposite = disclosable.some((m) => m.hasSyntheticEdits === true || m.type === 'canvas')
  return anyComposite ? 'composite' : 'generated'
}

/**
 * What we detected in the media, as opposed to what the user asked for. Shown in
 * the UI so an automatic assumption is legible rather than mysterious, and so a
 * user who switches disclosure on over media we found nothing in can see that
 * they are making a claim we cannot corroborate.
 */
export type AiProvenanceDetection = 'none' | 'edited' | 'generated'

/**
 * Reads provenance directly rather than going through `resolveAiInvolvement`.
 *
 * That function deliberately reports `createdWithAi: true` for media with no AI
 * provenance at all, because Facebook's `provenance_info` has to declare
 * *something* once the user has asked for disclosure. Borrowing it here was a
 * bug: it made a plain photograph report as "generated", so the label default
 * came out "AI generated" on a human photo.
 */
export const detectAiProvenance = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>>,
): AiProvenanceDetection => {
  if (media.some((m) => m.origin === 'ai_generated')) return 'generated'
  if (media.some((m) => m.hasSyntheticEdits === true)) return 'edited'
  return 'none'
}

/**
 * Default text for the baked label, derived from what we detected.
 *
 * The label used to be a single constant reading "AI generated", so an AI edit of
 * a real photograph shipped with a pill overstating what happened to it.
 *
 * Keys off generation rather than the IPTC claim: a flattened canvas built around
 * a generated image is `composite` for provenance purposes, but what a viewer
 * needs to know is that the imagery is synthetic, so it reads "AI generated".
 *
 * `none` falls back to the broader wording. The user has explicitly asked for a
 * label on media we found no AI in, so we have to render something, and the
 * broader claim is the safer of two guesses — but the UI states what we detected
 * so the guess is visible and editable rather than silent. That is the opposite
 * choice from the IPTC marker, which claims nothing at all in this case, because
 * that one is automatic and this one was requested.
 */
export const defaultAiLabelText = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>>,
): string => (detectAiProvenance(media) === 'edited' ? 'AI edited' : 'AI generated')

/** The shape of AI involvement, for platforms that distinguish generation from
 *  editing (Facebook's `provenance_info`). The user-facing control is a level,
 *  not a questionnaire, so this is derived from media provenance. */
export type AiInvolvement = { createdWithAi: boolean; editedWithAi: boolean }

/**
 * Resolves how AI was involved across a post's media. Falls back to
 * `createdWithAi` when disclosure is on but provenance says nothing — an
 * explicit user override is a claim we should pass on, not discard.
 */
export const resolveAiInvolvement = (
  media: ReadonlyArray<Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>>,
): AiInvolvement => {
  const createdWithAi = media.some((m) => m.origin === 'ai_generated')
  const editedWithAi = media.some((m) => m.hasSyntheticEdits === true)
  if (!createdWithAi && !editedWithAi) return { createdWithAi: true, editedWithAi: false }
  return { createdWithAi, editedWithAi }
}
